import {isAxiosError} from 'axios';

import {MflError, MflUserEntityErrorCode} from '../models/mfl';

import {capitalizeFirstLetter} from './string';

const apiFieldNameToFriendlyName = (apiFieldName = '') =>
  capitalizeFirstLetter(apiFieldName.replace('_', ' '));

const extractFieldNameFromSourcePointer = (prefix: string, sourcePointer = '') =>
  sourcePointer.substring(prefix.length).split('/').map(apiFieldNameToFriendlyName).join('/');

/**
 * MFL entity: user_identity. Source locators examples:
 * - /user_identity/first_name -> First name
 * - /user_identity/last_name -> Last name
 * - /user_identity/password -> Password
 * - /user_identity/billing_address/lines -> Billing address lines
 * - /user_identity/billing_address/locality -> Billing address locality
 * - /user_identity/birth_date -> Birth date
 */
function getErrorMessage(prefix: string, error: MflError) {
  const defaultErrorMessage = 'Unable to create user.';

  const detail = error.detail ? capitalizeFirstLetter(error.detail) : '';
  const sourcePointer = error.source?.pointer;
  const fieldName = extractFieldNameFromSourcePointer(prefix, sourcePointer);
  const errorMessage = fieldName && detail ? `${fieldName}: ${detail}` : defaultErrorMessage;

  return errorMessage;
}

const getCreateOrUpdateUserErrorMessage = (error: MflError) =>
  getErrorMessage('/user_identity/', error);

export const getCreateOrUpdateUserErrorMessages = (errors: MflError[] = []) =>
  errors.map(getCreateOrUpdateUserErrorMessage).join('.');

export const isUnhandledMFL500Error = (error: string = '') =>
  error.includes('something went wrong (500)');

export const hasWeakPasswordError = (errors: MflError[] = []) =>
  errors.some(
    e =>
      e.code === MflUserEntityErrorCode.sign_up_form_password_too_weak ||
      e.code === MflUserEntityErrorCode.password_too_weak,
  );

/**
 * Extract error message from API error or generic error
 * Handles both AxiosError (with response.data.errors) and standard Error
 */
export const getApiErrorMessage = (error: unknown): string => {
  // Handle AxiosError with MFL error format
  if (isAxiosError(error) && error.response?.data?.errors?.[0]?.detail) {
    return error.response.data.errors[0].detail;
  }

  // Handle standard Error
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback for unknown error types
  return 'An unknown error occurred';
};
